import Link from 'next/link'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import type { ListingSummary } from '@/lib/feedbridge/types'
import {
  formatAddress,
  formatSoldPrice,
  generateAddressSlug,
  generateListingPath,
} from '@/lib/feedbridge/helpers'
import { OffMarketCardWrapper } from './OffMarketCardWrapper'
import { ListingImage } from './ListingImage'
import { CardImageSlider } from './CardImageSlider'

interface ListingCardProps {
  listing: ListingSummary
  features?: Record<string, unknown>
  underOffer?: boolean
  showPrice?: boolean
  compact?: boolean
  thumbnailUrl?: string | null
  /** First three card-sized images — renders the arrow slider (Asana 022) */
  thumbnailUrls?: string[] | null
  /** Inspection time string, e.g. "Thu 19 Mar, 12:00pm - 12:30pm" */
  inspectionTime?: string | null
  /** Show the "View Property" CTA row — used on FeaturedListings Inspection variant */
  showCta?: boolean
}

function getCardPrice(listing: ListingSummary): string | null {
  if (listing.listing_status === 'leased') return null
  // Sold cards carry the SALE price, or "Price Withheld" — never the asking price.
  if (listing.listing_status === 'sold') return formatSoldPrice(listing)
  if (listing.listing_vertical === 'rental') {
    const disp = listing.rent_display?.trim()
    if (!disp || /^(yes|no|true|false)$/i.test(disp)) return null
    if (/[$a-zA-Z]/.test(disp)) return disp // already formatted, e.g. "$595 per week"
    if (/^\d[\d,]*$/.test(disp))
      return `$${Number(disp.replace(/,/g, '')).toLocaleString()} per week`
    return disp
  }
  const display =
    listing.price_display && !/^(yes|no|true|false)$/i.test(listing.price_display)
      ? listing.price_display
      : null
  return display || listing.price_view || null
}

function getCount(features: Record<string, unknown>, key: string): number {
  const val = features[key]
  if (typeof val === 'number') return val
  if (typeof val === 'string') {
    const num = parseInt(val, 10)
    return isNaN(num) ? 0 : num
  }
  return 0
}

export function ListingCard({
  listing,
  features,
  inspectionTime,
  showCta = false,
  thumbnailUrl,
  thumbnailUrls,
}: ListingCardProps) {
  const address = formatAddress(listing)
  const isOffMarket = listing.listing_status === 'offmarket'
  const price = getCardPrice(listing)

  // Title-case suburb
  const suburb = listing.address_suburb
    ?.toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

  // Street line: e.g. "10 Fairweather Avenue,"
  const streetLine = isOffMarket ? null : address.line1 ? `${address.line1},` : null

  const slug = generateAddressSlug(listing)
  // Exact-listing path (~id suffix): one address can hold several listings (e.g. a sold
  // sale + a current rental), so the card must link to precisely the listing it shows.
  const href = generateListingPath(listing)
  // View Transitions API — named per-listing so the card image morphs into
  // the property detail hero. Disabled when JS hasn't hydrated; CSS-only.
  const vtName = `listing-${slug}`

  // Feature counts
  const bedrooms = features ? getCount(features, 'bedrooms') : 0
  const bathrooms = features ? getCount(features, 'bathrooms') : 0
  const garages = features ? getCount(features, 'garages') || getCount(features, 'carports') : 0
  // Land area is merged into the features map by buildCardFeatures — the
  // /api/listings summary payload never carries it, only the listing detail does.
  const landAreaValue =
    features && typeof features.landAreaValue === 'number' ? features.landAreaValue : null
  const landAreaUnit =
    features && typeof features.landAreaUnit === 'string' ? features.landAreaUnit : null
  const landArea = landAreaValue
    ? `${landAreaValue.toLocaleString('en-AU', { maximumFractionDigits: 0 })}${landAreaUnit === 'squareMeter' || landAreaUnit === 'sqm' || !landAreaUnit ? 'm2' : ' ' + landAreaUnit}`
    : null
  // Residential display (Asana 009): no "Land" word, ALL-CAPS unit, hectare → HA.
  // Commercial keeps the lowercase `landArea` string + LAND/FLOOR words below.
  const landUnitCaps =
    landAreaUnit === 'squareMeter' || landAreaUnit === 'sqm' || !landAreaUnit
      ? 'M2'
      : /^hectares?$|^ha$/i.test(landAreaUnit)
        ? 'HA'
        : landAreaUnit.replace(/s$/i, '').toUpperCase()
  const landAreaResidential = landAreaValue
    ? `${landAreaValue.toLocaleString('en-AU', { maximumFractionDigits: 0 })}${landUnitCaps === 'M2' ? 'M2' : ' ' + landUnitCaps}`
    : null

  // Commercial card (Figma 2211:23036): category line + "16 CAR | 312m2 FLOOR | 644m2 LAND"
  const isCommercial = listing.listing_vertical === 'commercial'
  const floorAreaValue =
    features && typeof features.floorAreaValue === 'number' ? features.floorAreaValue : null
  const floorAreaUnit =
    features && typeof features.floorAreaUnit === 'string' ? features.floorAreaUnit : null
  const floorArea = floorAreaValue
    ? `${floorAreaValue.toLocaleString('en-AU', { maximumFractionDigits: 0 })}${floorAreaUnit === 'squareMeter' || floorAreaUnit === 'sqm' || !floorAreaUnit ? 'm2' : ' ' + floorAreaUnit}`
    : null
  const carSpaces = features && typeof features.carSpaces === 'number' ? features.carSpaces : null
  const commercialItems: string[] = isCommercial
    ? [
        ...(carSpaces ? [`${carSpaces} CAR`] : []),
        ...(floorArea ? [`${floorArea} FLOOR`] : []),
        ...(landArea ? [`${landArea} LAND`] : []),
      ]
    : []

  const hasFeatures = isCommercial
    ? commercialItems.length > 0
    : bedrooms > 0 || bathrooms > 0 || garages > 0 || !!landArea

  const card = (
    // Figma 2211:22948 (Desktop) gap/gap-6 24px · 2211:22986 (Mobile) gap/gap-4 16px
    <div
      className="group flex flex-col gap-4 md:gap-6 w-full transition-all duration-200"
      data-visual="property-card"
    >
      {/* Image container — ratio 4:3 (Image aspect ratio 2222:18667) */}
      <div
        className="relative w-full overflow-hidden loading-ground aspect-[4/3]"
        style={{ viewTransitionName: vtName }}
      >
        {/* Asana 022 — 3-image arrow slider; off-market cards stay single
            (they're blurred behind the sign-up gate). Slide 1 is ALWAYS the
            summary primary_image so the summary->enriched Suspense swap reuses
            the already-cached first frame (no flash); the enriched stage adds
            slides 2-3 in place. Controls follow the real image count — a
            single-photo (or placeholder) card shows no arrows at all
            (Steve 2026-08-21); `alwaysShowControls` was forcing them on. */}
        {!isOffMarket && (listing.primary_image || (thumbnailUrls && thumbnailUrls.length > 0)) ? (
          <CardImageSlider
            images={[
              listing.primary_image || thumbnailUrls![0],
              ...(thumbnailUrls || []).slice(1, 3),
            ].filter(Boolean)}
            alt={address.full || listing.headline || 'Property image'}
          />
        ) : (
          <div className="absolute inset-0">
            <ListingImage
              src={thumbnailUrl || listing.primary_image || null}
              alt={address.full || listing.headline || 'Property image'}
            />
          </div>
        )}
        {/* Off-market overlay — fill color/brand/wilson/neutral/950-base-50%
            (#00000080) per I2211:23032;2211:22950. */}
        {isOffMarket && (
          <div className="off-market-overlay absolute inset-0 bg-foreground/50 backdrop-blur-[8px] z-[1] pointer-events-none" />
        )}
        {/* Badge row 2211:22951 — flush with the image corner (left-0 top-0),
            gap/gap-0. Figma defines exactly one badge here: badge/Secondary
            (2211:22953) carrying the inspection time. There is NO status badge
            in any of the 22 Type×Breakpoint variants.
            Weight is Lato REGULAR 400 rather than the badge's Light 300 — Steve
            asked for the inspection time to read a little heavier at this size
            (2026-09-01). Deliberate deviation from badge/Secondary. */}
        {inspectionTime && (
          <div className="absolute top-0 left-0 z-[2] flex items-center">
            <span className="inline-flex items-center justify-center h-[22px] px-1.5 py-0.5 bg-secondary text-foreground text-body-xs font-normal uppercase whitespace-nowrap">
              Inspect {inspectionTime}
            </span>
          </div>
        )}
        {/* Off-market Sign Up button — I2211:23032;4017:73356 wrapper is p-2 on a
            justify-end/items-start image frame → bottom-left with an 8px inset. */}
        {isOffMarket && (
          <div className="absolute bottom-0 left-0 p-2 z-[3]">
            {/* Canonical Button Mini, type=primary — rendered as a span because the
                whole card is already an anchor. Two labels: OffMarketCardWrapper
                swaps them via descendant classes once the member is signed in. */}
            <span className="off-market-cta-signup">
              <span className={cn(buttonVariants({ size: 'mini' }))}>
                Sign Up To View Off-Market
              </span>
            </span>
            <span className="off-market-cta-view hidden">
              <span className={cn(buttonVariants({ size: 'mini' }))}>
                View Off-Market Opportunity
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Card info — Figma order: Address → Features → Price */}
      <div className="flex flex-col gap-2 w-full">
        {/* Address — Headings/headlineSm (Merriweather 400, font/size/lg →
            24/32 desktop · 22/28 tablet · 20/24 mobile via `text-display-lg`).
            Street line regular, suburb italic. Figma defines no hover state. */}
        <div className="flex flex-col font-display font-normal text-display-lg text-foreground w-full">
          {isOffMarket ? (
            <p className="w-full italic">{suburb}</p>
          ) : (
            <>
              {streetLine && <p className="w-full">{streetLine}</p>}
              {suburb && <p className="w-full italic">{suburb}</p>}
            </>
          )}
        </div>

        {/* Commercial category line — Figma 2211:23036 shows the property type
            ("Offices", "Retail", …) in Body/bodySm (14/16 light) between the
            address and the features row. Residential variants have no such line. */}
        {isCommercial && !isOffMarket && listing.category_name && (
          <p className="text-body-sm font-light text-foreground">{listing.category_name}</p>
        )}

        {/* Features row: "3 BED | 2 BATH | 2 CAR | 644m2 LAND" — number first,
            uppercase label, Lato regular, 16px. Commercial (2211:23036):
            "16 CAR | 312m2 FLOOR | 644m2 LAND". */}
        {hasFeatures && isCommercial && (
          <div className="flex items-center gap-1 text-body-md font-normal text-foreground whitespace-nowrap flex-wrap">
            {commercialItems.map((item, i) => (
              <span key={item} className="flex items-center gap-1">
                {i > 0 && <span>|</span>}
                <span>
                  {item.replace(/ (CAR|FLOOR|LAND)$/, '')}{' '}
                  <span className="uppercase">{item.split(' ').pop()}</span>
                </span>
              </span>
            ))}
          </div>
        )}
        {hasFeatures && !isCommercial && (
          <div className="flex items-center gap-1 text-body-md font-normal text-foreground whitespace-nowrap flex-wrap">
            {bedrooms > 0 && (
              <>
                <span className="flex items-center gap-1">
                  <span>{bedrooms}</span>
                  <span className="uppercase">Bed</span>
                </span>
                {(bathrooms > 0 || garages > 0 || !!landArea) && <span>|</span>}
              </>
            )}
            {bathrooms > 0 && (
              <>
                <span className="flex items-center gap-1">
                  <span>{bathrooms}</span>
                  <span className="uppercase">Bath</span>
                </span>
                {(garages > 0 || !!landArea) && <span>|</span>}
              </>
            )}
            {garages > 0 && (
              <>
                <span className="flex items-center gap-1">
                  <span>{garages}</span>
                  <span className="uppercase">Car</span>
                </span>
                {!!landArea && <span>|</span>}
              </>
            )}
            {landAreaResidential && (
              /* Asana 009 — value + CAPS unit only, no "Land" word */
              <span>{landAreaResidential}</span>
            )}
          </div>
        )}

        {/* Price — Lato regular, 16px, below features */}
        {price && <p className="text-body-md font-normal text-foreground">{price}</p>}

        {/* CTA row 2211:22983 "Inspection" — gap/gap-1 4px, no extra margin
            (the parent stack already supplies gap/gap-2). */}
        {showCta && (
          <div className="flex items-center gap-1">
            {/* Calendar icon button 2211:22984 — 24×24, theme/accent, radius sm
                (canonical icon-mini, primary). Span: the card is already an anchor. */}
            <span
              className={cn(buttonVariants({ className: 'shrink-0', size: 'icon-mini' }))}
              aria-label="Add inspection to calendar"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 13V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8" />
                <path d="M16 2v4" />
                <path d="M8 2v4" />
                <path d="M3 10h18" />
                <path d="M19 16v6" />
                <path d="M22 19l-3 3-3-3" />
              </svg>
            </span>
            {/* Button 2211:22985 — h24, p-2, gap-1, 1px theme/border, radius sm,
                shadow/xs, Body/bodySm 14/16 regular, theme/foreground
                (canonical outline × mini). */}
            <span className={cn(buttonVariants({ size: 'mini', variant: 'outline' }))}>
              View Property
            </span>
          </div>
        )}
      </div>
    </div>
  )

  if (isOffMarket) {
    return <OffMarketCardWrapper href={href}>{card}</OffMarketCardWrapper>
  }

  return (
    <Link href={href} className="block group">
      {card}
    </Link>
  )
}
